import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  useTableFilterSortSearchManager,
  useTableManageColumns,
  useTableFilterSortData,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useRows from './hooks.useRows';
import useHeaders from './hooks.useHeaders';
import useParseResults from './hooks.useParseResults';
import useModalProps from './hooks.useModalProps';
import useComplianceOverviewQuery from '../../hooks/data-fetching/useComplianceOverviewQuery';
import useComplianceOverviewFilterOptionsQuery from '../../hooks/data-fetching/useComplianceOverviewFilterOptionsQuery';
import useUserOrganizationId from '../../hooks/useUserOrganizationId';
import useDownloadCSVPDF, {
  adaptFiltersForBackend,
  buildReverseLookupFromOptions,
} from './hooks.useDownloadCSVPDF';
import usePlanningRangeFilter from './hooks.usePlanningRangeFilter';
import { COMPLIANCE_TYPES } from './constants';
import { PROPERTY_PATH_MAP } from './constantsV2';
import moment from 'moment-business-days';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import useCertStatusColumn from './hooks.useCertStatusColumn';

const DEFAULT_PER_PAGE = 50;

export const useComplianceOverview = ({ intl, isActive = true }) => {
  moment.updateLocale('us', {
    workingWeekdays: [1, 2, 3, 4, 5],
  });

  const organizationId = useUserOrganizationId();
  const { complianceSummaryNeedsReviewReviewOverdueColumn = false } =
    useFlags();

  const [complianceType, setComplianceTypeState] = useState(
    COMPLIANCE_TYPES.ALL_OPEN_CERTS,
  );
  const [includeNotStartedCerts, setIncludeNotStartedCerts] = useState(true);

  const isAllOpenCertsView = complianceType === COMPLIANCE_TYPES.ALL_OPEN_CERTS;
  const allOpenCertsTableName = useUniqueTableName(
    'compliance-overview_all-open-certs',
  );
  const needsReviewTableName = useUniqueTableName(
    'compliance-overview_needs-review',
  );
  const name = isAllOpenCertsView
    ? allOpenCertsTableName
    : needsReviewTableName;

  // Needs Review's filter options are derived from the fetched `results`,
  // which the fetch below can't see yet — a cycle. Break it with a ref
  // holding the last render's results: it lags by one render, a cosmetic
  // delay, not an accuracy issue. All Open Certs' options and its reverse
  // lookup come from the filter-options endpoint below instead.
  const lastKnownResultsRef = useRef([]);

  const { filterOptions: allOpenCertsServerFilterOptions } =
    useComplianceOverviewFilterOptionsQuery({
      organizationId,
      includeNotStartedCerts,
      options: { enabled: isActive && Boolean(organizationId) },
    });

  // Manage Headers
  const allOpenCertsHeaders = useHeaders({
    results: lastKnownResultsRef.current,
    serverFilterOptions: allOpenCertsServerFilterOptions,
    intl,
    complianceType,
    includeNotStartedCerts,
    complianceSummaryNeedsReviewReviewOverdueColumn,
  });
  const needsReviewHeaders = useHeaders({
    results: lastKnownResultsRef.current,
    intl,
    complianceType: COMPLIANCE_TYPES.NEEDS_REVIEW,
    includeNotStartedCerts: false,
    complianceSummaryNeedsReviewReviewOverdueColumn,
  });
  const headers = isAllOpenCertsView ? allOpenCertsHeaders : needsReviewHeaders;

  // Manage Modal
  const ModalProps = useModalProps();

  const allOpenCertsTableState = useTableFilterSortSearchManager({
    name: allOpenCertsTableName,
    initialOrderBy: 'voucherEffectiveDate',
    initialOrder: 'desc',
    headers: allOpenCertsHeaders,
  });
  const needsReviewTableState = useTableFilterSortSearchManager({
    name: needsReviewTableName,
    initialOrderBy: 'uploadDate',
    initialOrder: 'desc',
    headers: needsReviewHeaders,
  });
  const activeTableState = isAllOpenCertsView
    ? allOpenCertsTableState
    : needsReviewTableState;
  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = activeTableState;

  // Server-side paging (All Open Certs only). Reads allOpenCertsTableState
  // directly rather than activeTableState: that tab's filter/sort/search can
  // only change while it's visible, so this stays correct either way.
  const pageResetFingerprint = JSON.stringify({
    dateState: allOpenCertsTableState.dateState,
    filterState: allOpenCertsTableState.filterState,
    order: allOpenCertsTableState.order,
    orderBy: allOpenCertsTableState.orderBy,
    searchState: allOpenCertsTableState.searchState,
  });
  // The page is pinned to the view it was picked in and derived during
  // render, so a filter change never lets a request for the old page under
  // the new filter out before the reset to page 1.
  const [pageSelection, setPageSelection] = useState({
    page: 1,
    fingerprint: pageResetFingerprint,
  });
  const page =
    pageSelection.fingerprint === pageResetFingerprint ? pageSelection.page : 1;

  const handlePageChange = useCallback(
    (_event, newPage) => {
      setPageSelection({ page: newPage, fingerprint: pageResetFingerprint });
    },
    [pageResetFingerprint],
  );

  // Both useTableFilterSortSearchManager calls above run unconditionally, so
  // the two tabs' filter-manager instances stay mounted the whole time —
  // switching tabs does NOT unmount/remount either one, and recertRange only
  // ever lives on allOpenCertsTableState's filterState (only the All Open
  // Certs tab renders the PlanningRangeFilter control). Clear it explicitly
  // on every tab change so a planning-range selection doesn't silently
  // persist across a trip to Needs Review and back.
  const handleComplianceTypeChange = useCallback(
    (newComplianceType) => {
      allOpenCertsTableState.handleFilterChange('recertRange', []);
      setPageSelection((prev) => ({ ...prev, page: 1 }));
      setComplianceTypeState(newComplianceType);
    },
    [allOpenCertsTableState],
  );

  // Feeds both the table's server-side fetch and the CSV/PDF export
  // (hooks.useDownloadCSVPDF, via the same adaptFiltersForBackend) so both
  // always agree on what filter/sort/search params "the current view" means.
  const { currentFilter, currentSorting, searchText } = useMemo(() => {
    const reverseLookup = buildReverseLookupFromOptions(
      allOpenCertsServerFilterOptions,
    );
    return adaptFiltersForBackend({
      dateState: allOpenCertsTableState.dateState,
      filterState: allOpenCertsTableState.filterState,
      order: allOpenCertsTableState.order.toUpperCase(),
      orderBy: allOpenCertsTableState.orderBy,
      searchState: allOpenCertsTableState.searchState,
      includeNotStartedCerts,
      reverseLookup,
    });
  }, [
    allOpenCertsTableState.dateState,
    allOpenCertsTableState.filterState,
    allOpenCertsTableState.order,
    allOpenCertsTableState.orderBy,
    allOpenCertsTableState.searchState,
    allOpenCertsServerFilterOptions,
    includeNotStartedCerts,
  ]);

  // Fetch data
  const {
    results,
    isLoading,
    isFetching,
    totalCount: serverTotalCount,
    pageCount,
    perPage,
  } = useComplianceOverviewQuery({
    organizationId,
    complianceType,
    includeNotStartedCerts,
    page,
    perPage: DEFAULT_PER_PAGE,
    sortBy: currentSorting.fieldName,
    sortOrder: currentSorting.order,
    filter: currentFilter,
    searchText,
    options: {
      enabled: isActive,
    },
  });

  // See the note above allOpenCertsHeaders/needsReviewHeaders.
  useEffect(() => {
    lastKnownResultsRef.current = results;
  }, [results]);

  // Parse results
  const parsedResults = useParseResults({
    results,
    complianceType,
    complianceSummaryNeedsReviewReviewOverdueColumn,
  });

  // All Open Certs is already filtered/sorted/paged server-side; only Needs
  // Review still needs the client-side pass. Feeding it `[]` on the other
  // tab skips the actual work without conditionally calling the hook.
  const needsReviewFilteredResults = useTableFilterSortData({
    results: isAllOpenCertsView ? [] : parsedResults,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP: PROPERTY_PATH_MAP(includeNotStartedCerts) ?? {},
  });
  const sortedAndFilteredResults = isAllOpenCertsView
    ? parsedResults
    : needsReviewFilteredResults;

  const rows = useRows({
    sortedAndFilteredResults,
    ModalProps,
    includeNotStartedCerts,
  });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
  } = useTableManageColumns({
    name,
    headers,
    firstRow: rows[0],
  });

  const {
    isCertStatusVisible,
    toggleCertStatusColumn,
    shouldNotStartedCertsButtonBeDisplayed,
  } = useCertStatusColumn({
    selectedColumns,
    handleColumnChange,
    setIncludeNotStartedCerts,
  });

  const { handleDownloadCSVPDF, isCSVPDFExportLoading } = useDownloadCSVPDF({
    complianceType,
    dateState,
    filterState,
    intl,
    order,
    orderBy,
    organizationId,
    searchState,
    includeNotStartedCerts,
    results: parsedResults,
    serverFilterOptions: isAllOpenCertsView
      ? allOpenCertsServerFilterOptions
      : undefined,
  });

  const shouldPlanningRangeFilterBeDisplayed = isAllOpenCertsView;

  const { planningRangeOptions } = usePlanningRangeFilter({
    intl,
    organizationId,
    includeNotStartedCerts,
    filter: currentFilter,
    searchText,
    enabled: isActive && shouldPlanningRangeFilterBeDisplayed,
  });

  // keepPreviousData keeps isLoading false during a refetch, so track it
  // separately for the All Open Certs loading indicator.
  const isRefreshing = isAllOpenCertsView && isFetching && !isLoading;
  const isBusy = isLoading || isRefreshing;

  const onPlanningRangeChange = useCallback(
    (value) => handleFilterChange('recertRange', value),
    [handleFilterChange],
  );

  // Reads allOpenCertsTableState directly so the count is correct
  // regardless of which tab is active. Each selected planning tile counts
  // on its own; every other column counts once.
  const activeFilterCount = useMemo(() => {
    const filterCount = Object.entries(
      allOpenCertsTableState.filterState ?? {},
    ).reduce((count, [key, value]) => {
      if (key === 'recertRange') {
        return count + (Array.isArray(value) ? value.length : 0);
      }
      const isActive = Array.isArray(value) ? value.length > 0 : Boolean(value);
      return isActive ? count + 1 : count;
    }, 0);
    const searchCount = Object.values(
      allOpenCertsTableState.searchState ?? {},
    ).filter(Boolean).length;
    const dateCount = Object.values(
      allOpenCertsTableState.dateState ?? {},
    ).filter(
      (bound) => Boolean(bound?.fromDate) || Boolean(bound?.toDate),
    ).length;
    return filterCount + searchCount + dateCount;
  }, [
    allOpenCertsTableState.filterState,
    allOpenCertsTableState.searchState,
    allOpenCertsTableState.dateState,
  ]);

  const handleClearFilters = useCallback(() => {
    Object.keys(allOpenCertsTableState.filterState ?? {}).forEach((key) =>
      allOpenCertsTableState.handleFilterChange(key, []),
    );
    Object.keys(allOpenCertsTableState.searchState ?? {}).forEach((key) =>
      allOpenCertsTableState.handleSearchSubmit(key, ''),
    );
    Object.keys(allOpenCertsTableState.dateState ?? {}).forEach((key) =>
      allOpenCertsTableState.handleDateSubmit(key, null, null),
    );
  }, [allOpenCertsTableState]);

  return {
    allColumnsHidden,
    columnOptions,
    complianceType,
    count: rows.length ?? 0,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleDownloadCSVPDF,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    isLoading,
    isRefreshing,
    isBusy,
    isCSVPDFExportLoading,
    ModalProps,
    name,
    order,
    orderBy,
    rows,
    searchState,
    selectedColumns,
    setComplianceType: handleComplianceTypeChange,
    sortedAndFilteredResults,
    totalCount: isAllOpenCertsView
      ? serverTotalCount ?? 0
      : results.length ?? null,
    isCertStatusVisible,
    toggleCertStatusColumn,
    shouldNotStartedCertsButtonBeDisplayed,
    planningRangeOptions,
    selectedPlanningRange: filterState.recertRange ?? [],
    onPlanningRangeChange,
    shouldPlanningRangeFilterBeDisplayed,
    activeFilterCount,
    handleClearFilters,
    // Pagination only applies to the server-paged All Open Certs tab.
    ...(isAllOpenCertsView
      ? {
          currentPage: page,
          perPage,
          pageCount,
          onPageChange: handlePageChange,
        }
      : {}),
  };
};
